/**
 * useInventoryInsights — On-demand AI-powered inventory insights for a portco.
 *
 * On mount, silently checks for cached results (no Claude API call).
 * Call `fetch()` to trigger a full analysis.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  getInventoryInsights,
  getCachedInventoryInsights,
  InventoryInsightsResponse,
} from "../utils/PowerBIApiCall";

export interface InventoryInsightsState {
  data: InventoryInsightsResponse | null;
  loading: boolean;
  error: string | null;
  fetch: () => void;
}

export function useInventoryInsights(
  portcoId: string | undefined,
): InventoryInsightsState {
  const [data, setData] = useState<InventoryInsightsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<(() => void) | undefined>(undefined);

  // On mount, check for cached results (lightweight GET, no Claude API call)
  useEffect(() => {
    if (!portcoId) return;
    let cancelled = false;

    getCachedInventoryInsights(portcoId).then((cached) => {
      if (!cancelled && cached) setData(cached);
    });

    return () => {
      cancelled = true;
    };
  }, [portcoId]);

  const fetch = useCallback(() => {
    if (!portcoId || loading) return;

    // Cancel any in-flight request
    cancelRef.current?.();
    let cancelled = false;
    cancelRef.current = () => {
      cancelled = true;
    };

    async function loadInsights() {
      try {
        setLoading(true);
        setError(null);

        const result = await getInventoryInsights(portcoId!);

        if (cancelled) return;
        setData(result);
      } catch (e) {
        if (cancelled) return;
        setError(
          e instanceof Error ? e.message : "Failed to load inventory insights",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadInsights();
  }, [portcoId, loading]);

  return { data, loading, error, fetch };
}
